import React, { useMemo } from "react";
import {
  Stack,
  Divider,
  Typography,
  Box,
  IconButton,
  Button,
  alpha,
} from "@mui/material";
import { Formik, Form, FieldArray, FormikProps } from "formik";
import * as Yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSearchSelect from "../components/common/FormSearchSelect";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createExpense } from "../redux/actions/expenseActions";
import { formatCurrency } from "../utils/currencyUtils";

import { ExpenseRequestDto } from "../shared/dtos/expense/ExpenseRequestDto";
import { expenseActions } from "../redux/slices/expense";
// 1. Define Types for Form Values
interface PaymentValue {
  paymentMethodId: string;
  amount: number | "";
}

interface ExpenseFormValues {
  categoryId: string;
  amount: number | "";
  description: string;
  payments: PaymentValue[];
}

// 2. Validation Schema
const expenseSchema = Yup.object().shape({
  categoryId: Yup.string().required("Category is required"),
  amount: Yup.number()
    .required("Amount is required")
    .min(0.01, "Amount must be greater than zero"),
  description: Yup.string().required("Description/Reason is required"),
  payments: Yup.array()
    .of(
      Yup.object().shape({
        paymentMethodId: Yup.string().required("Payment method required"),
        amount: Yup.number().required("Amount required").min(0.01),
      }),
    )
    .min(1, "At least one payment method must be provided"),
});

interface CreateExpenseFormProps {
  shiftId?: string | null;
  loading: boolean;
  error: string | null;
}

const CreateExpenseForm: React.FC<CreateExpenseFormProps> = ({
  shiftId,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: expenseCategories } = useAppSelector(
    (state) => state.expenseCategory,
  );
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );

  const categoryOptions = useMemo(
    () =>
      (expenseCategories || []).map((c) => ({ value: c.id, label: c.name })),
    [expenseCategories],
  );

  const pmOptions = useMemo(
    () =>
      (paymentMethods || []).map((pm) => ({ value: pm.id, label: pm.name })),
    [paymentMethods],
  );

  const initialValues: ExpenseFormValues = {
    categoryId: "",
    amount: "",
    description: "",
    payments: [{ paymentMethodId: "", amount: "" }],
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(expenseActions.setErrorCreate(null))}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={expenseSchema}
        onSubmit={(values) => {
          const payload: ExpenseRequestDto = {
            categoryId: values.categoryId,
            amount: Number(values.amount),
            description: values.description,
            payments: values.payments
              .filter((p) => Number(p.amount) > 0)
              .map((p) => ({
                paymentMethodId: p.paymentMethodId,
                amount: Number(p.amount),
              })),
          };

          if (shiftId) payload.shiftId = shiftId;
          dispatch(createExpense(payload));
        }}
      >
        {({ values, isValid, dirty }: FormikProps<ExpenseFormValues>) => {
          const totalPayments = values.payments.reduce(
            (sum, p) => sum + (Number(p.amount) || 0),
            0,
          );
          const expectedAmount = Number(values.amount) || 0;

          // Use epsilon comparison for floating point math safety
          const isPaymentBalanced =
            Math.abs(totalPayments - expectedAmount) < 0.01;

          return (
            <Form>
              <Stack spacing={3}>
                {/* --- TOP SECTION: CSS GRID VIA BOX --- */}
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    // One column on mobile, two on small+ screens
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormSearchSelect
                    name="categoryId"
                    label="Expense Category"
                    options={categoryOptions}
                  />
                  <FormTextField
                    name="amount"
                    label="Total Expense Amount"
                    type="number"
                  />

                  {/* Full width row for description */}
                  <Box sx={{ gridColumn: { xs: "1fr", sm: "span 2" } }}>
                    <FormTextField
                      name="description"
                      label="Description / Reason"
                      multiline
                      rows={2}
                    />
                  </Box>
                </Box>

                <Divider>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    FUNDING SOURCES
                  </Typography>
                </Divider>

                {/* --- DYNAMIC PAYMENTS SECTION --- */}
                <FieldArray name="payments">
                  {({ push, remove }) => (
                    <Stack spacing={2}>
                      {values.payments.map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "grid",
                            gap: 2,
                            // Responsive grid for the row
                            gridTemplateColumns: {
                              xs: "1fr auto",
                              sm: "2fr 1fr auto",
                            },
                            alignItems: "start",
                          }}
                        >
                          <Box
                            sx={{ gridColumn: { xs: "span 1", sm: "span 1" } }}
                          >
                            <FormSelectField
                              name={`payments.${index}.paymentMethodId`}
                              label="Payment Method"
                              options={pmOptions}
                            />
                          </Box>
                          <Box>
                            <FormTextField
                              name={`payments.${index}.amount`}
                              label="Amount"
                              type="number"
                            />
                          </Box>
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            disabled={values.payments.length === 1}
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}

                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          push({ paymentMethodId: "", amount: "" })
                        }
                        sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                      >
                        Add Split Payment
                      </Button>
                    </Stack>
                  )}
                </FieldArray>

                {/* --- SUMMARY BOX --- */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      alpha(theme.palette.background.default, 0.5),
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total Expense:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(expectedAmount)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total Allocated:
                      </Typography>
                      <Typography
                        variant="body2"
                        color={
                          isPaymentBalanced ? "success.main" : "error.main"
                        }
                        sx={{ fontWeight: "bold" }}
                      >
                        {formatCurrency(totalPayments)}
                      </Typography>
                    </Stack>

                    {!isPaymentBalanced && expectedAmount > 0 && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{
                          textAlign: "right",
                          display: "block",
                          mt: 1,
                          fontWeight: 500,
                        }}
                      >
                        The allocated total must equal the expense amount.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <SubmitButton
                  title="Record Expense"
                  loading={loading}
                  disabled={!dirty || !isValid || !isPaymentBalanced || loading}
                  startIcon={<SaveOutlinedIcon />}
                  fullWidth
                />
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateExpenseForm;
